<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import type { ContractManagementCalendarInDb, ContractManagementCalendarLinkResult } from '@/api';
import { CalendarService } from '@/services/calendarService';
import { useCopyToClipboard } from '@/composables/useCopyToClipboard';
import { useListQuerySync } from '@/composables/useListQuerySync';
import { useAuthStore } from '@/stores/auth';
import { logger } from '@/utils/logger';
import Layout from '@/components/design/layout/Layout.vue';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaCodeBox from '@/components/design/base/GaiaCodeBox.vue';
import GaiaFieldRow from '@/components/design/base/GaiaFieldRow.vue';
import GaiaHeader from '@/components/design/base/GaiaHeader.vue';
import GaiaIconButton from '@/components/design/base/GaiaIconButton.vue';
import GaiaSpinner from '@/components/design/base/GaiaSpinner.vue';
import GaiaTag from '@/components/design/base/GaiaTag.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaTooltip from '@/components/design/base/GaiaTooltip.vue';
import GaiaBanner from '@/components/design/composite/GaiaBanner.vue';
import GaiaEmptyState from '@/components/design/composite/GaiaEmptyState.vue';
import GaiaPopconfirm from '@/components/design/composite/GaiaPopconfirm.vue';
import GaiaRemovableList, {
  type GaiaRemovableItem,
} from '@/components/design/composite/GaiaRemovableList.vue';
import GaiaSectionHeader from '@/components/design/base/GaiaSectionHeader.vue';
import GaiaTabBar, { type GaiaTabBarItem } from '@/components/design/composite/GaiaTabBar.vue';
import GaiaCalendarEventsTable from '@/components/design/domain/contractManagement/calendar/GaiaCalendarEventsTable.vue';
import GaiaCalendarMonthPreview from '@/components/design/domain/contractManagement/calendar/GaiaCalendarMonthPreview.vue';
import GaiaCalendarPreviewFilters from '@/components/design/domain/contractManagement/calendar/GaiaCalendarPreviewFilters.vue';
import MassShareContractsModal from '@/components/contractManagement/MassShareContractsModal.vue';
import {
  emptyCalendarPreviewFilters,
  type CalendarPreviewFilters,
} from '@/utils/calendarOccurrences';
import { DATE_TYPE_LABELS } from '@/services/contractManagement/dateRow';
import { NO_CONTRACT_TYPE_LABEL } from '@/components/design/statusMaps';
import GaiaSearchInput from '@/components/design/base/inputs/GaiaSearchInput.vue';
import {
  gaiaContentWidth,
  gaiaNeutral,
  gaiaTextButtonOverrides,
} from '@/components/design/themes/tokens';
import { navLabels } from '@/components/design/domain/navigation/navLabels';
import { IconChevronDown, IconChevronForward, IconShare } from '@/components/design/icons';
import { useGaiaToast } from '@/composables/useGaiaToast';

/**
 * A single calendar (Calendar Events mock): what it is configured to extract,
 * a live preview of the dates it found, and the collections it is linked to.
 *
 * Non-admins reach this page — the route only needs the contract-management
 * feature — so everything that mutates is gated in the template rather than
 * assumed.
 */

const route = useRoute();
const router = useRouter();
const message = useGaiaToast();
const authStore = useAuthStore();
const { copy } = useCopyToClipboard();

const calendarUid = computed(() => route.params.uid as string);
/**
 * Creating, updating and deleting a calendar — and linking anything to it —
 * are org-admin endpoints. Rotating the .ics link is not: that token is the
 * caller's own, so anyone who can read the calendar may rotate theirs.
 */
const canManage = computed(() => authStore.userPermissions.is_org_admin);
const ADMIN_ONLY_HINT =
  'Only organisation admins can change a calendar. Ask an admin in your workspace.';

const calendar = ref<ContractManagementCalendarInDb | null>(null);
const loading = ref<boolean>(false);
const loadError = ref<string | null>(null);

/** The configuration card starts folded: the preview is what people come for. */
const configExpanded = ref<boolean>(false);

// One set of filters narrows the calendar and the list beneath it alike.
const previewFilters = ref<CalendarPreviewFilters>(emptyCalendarPreviewFilters());

const fixedDateLabels = computed(() =>
  (calendar.value?.date_types ?? []).map(type => DATE_TYPE_LABELS[type] ?? type)
);

// The month and the list load separately; a date corrected or extracted in
// one refreshes the other.
const monthPreviewRef = ref<{ refresh: () => void }>();
const eventsTableRef = ref<{ refresh: () => void }>();

// ── Sources ─────────────────────────────────────────────────────────────────

const sourceDirectoryNames = ref<string[]>([]);
const sourceContractCount = ref<number>(0);
const sourcesError = ref<boolean>(false);

async function fetchSources(): Promise<void> {
  try {
    const [directories, contracts] = await Promise.all([
      CalendarService.listSourceDirectories(calendarUid.value),
      CalendarService.listSourceContracts(calendarUid.value),
    ]);
    sourceDirectoryNames.value = directories.map(directory => directory.directory_name);
    sourceContractCount.value = contracts.length;
  } catch (error) {
    logger.error('Error loading calendar sources:', error);
    sourcesError.value = true;
  }
}

/** What the calendar reads: its search, or the folders and contracts it picked. */
const sourceTags = computed<string[]>(() => {
  const criteria = calendar.value?.contract_criteria;
  if (criteria) {
    const text = criteria.text?.trim();
    const types = criteria.column_filters?.type ?? [];
    return [
      ...(text ? [`Search “${text}”`] : []),
      ...types.map(type => `Type: ${type || NO_CONTRACT_TYPE_LABEL}`),
      ...(criteria.column_filters?.tags ?? []).map(tag => `Tag: ${tag}`),
    ];
  }
  const contractCount = sourceContractCount.value;
  return [
    ...sourceDirectoryNames.value.map(name => `Folder: ${name}`),
    ...(contractCount > 0 ? [`${contractCount} contract${contractCount === 1 ? '' : 's'}`] : []),
  ];
});

// ── Linked collections ──────────────────────────────────────────────────────

interface LinkedItem {
  uid: string;
  label: string;
  description?: string | null;
}

interface LinkedCollection {
  key: string;
  label: string;
  emptyText: string;
  items: LinkedItem[];
  loading: boolean;
  /** Set when this tab's list could not be fetched. Shown in the tab, not as a toast. */
  error: string | null;
  list: (uid: string) => Promise<LinkedItem[]>;
  /**
   * Only viewers are removed here — they are added through Share. Everything
   * else is edited in the wizard, so those tabs are read-only.
   */
  remove?: (uid: string, keys: string[]) => Promise<ContractManagementCalendarLinkResult>;
}

function toUserItem(user: { uid: string; full_name: string; email: string }): LinkedItem {
  return { uid: user.uid, label: user.full_name || user.email, description: user.email };
}

/**
 * Viewer users and roles share one tab, as they share one picker in Share.
 * Keyed with a prefix so a remove can tell the two apart.
 */
const VIEWER_USER_PREFIX = 'user:';
const VIEWER_ROLE_PREFIX = 'role:';

async function listViewers(uid: string): Promise<LinkedItem[]> {
  const [users, roles] = await Promise.all([
    CalendarService.listPermissionUsers(uid),
    CalendarService.listPermissionRoles(uid),
  ]);
  return [
    ...users.map(user => {
      const item = toUserItem(user);
      return { ...item, uid: `${VIEWER_USER_PREFIX}${item.uid}` };
    }),
    ...roles.map(role => ({
      uid: `${VIEWER_ROLE_PREFIX}${role.uid}`,
      label: role.name,
      description: 'Role',
    })),
  ];
}

async function removeViewers(
  uid: string,
  keys: string[]
): Promise<ContractManagementCalendarLinkResult> {
  const userUids = keys
    .filter(key => key.startsWith(VIEWER_USER_PREFIX))
    .map(key => key.slice(VIEWER_USER_PREFIX.length));
  const roleUids = keys
    .filter(key => key.startsWith(VIEWER_ROLE_PREFIX))
    .map(key => key.slice(VIEWER_ROLE_PREFIX.length));
  const results = await Promise.all([
    ...(userUids.length > 0 ? [CalendarService.removePermissionUsers(uid, userUids)] : []),
    ...(roleUids.length > 0 ? [CalendarService.removePermissionRoles(uid, roleUids)] : []),
  ]);
  return {
    total: results.reduce((sum, result) => sum + result.total, 0),
    successful: results.reduce((sum, result) => sum + result.successful, 0),
    failed: results.reduce((sum, result) => sum + result.failed, 0),
    results: results.flatMap(result => result.results),
  };
}

async function listReceivers(uid: string): Promise<LinkedItem[]> {
  const users = await CalendarService.listReceivers(uid);
  return users.map(toUserItem);
}

/** One descriptor per tab. */
const collections = ref<LinkedCollection[]>([
  {
    key: 'viewers',
    label: 'Viewers',
    emptyText: 'Only organisation admins can see this calendar. Share it to add viewers.',
    items: [],
    loading: false,
    error: null,
    list: listViewers,
    remove: removeViewers,
  },
  {
    key: 'receivers',
    label: 'Receivers',
    emptyText: 'Nobody receives the reminder emails. Add receivers under Edit.',
    items: [],
    loading: false,
    error: null,
    list: listReceivers,
  },
]);

// Only the tab param is used here — the composable omits every value that is
// still its default, so no page/sort noise reaches the URL.
const { activeTab } = useListQuerySync({
  defaultTab: 'viewers',
  allowedTabs: collections.value.map(entry => entry.key),
});

const activeCollection = computed(
  () => collections.value.find(entry => entry.key === activeTab.value) ?? collections.value[0]
);

/**
 * Receiver emails live on the calendar itself rather than as a link, but they
 * receive the digest just like the linked users — so the Receivers tab lists
 * them too.
 */
const externalReceiverItems = computed<LinkedItem[]>(() =>
  (calendar.value?.receiver_emails ?? []).map(email => ({
    uid: `email:${email}`,
    label: email,
    description: 'External email',
  }))
);

function itemsOf(collection: LinkedCollection): LinkedItem[] {
  return collection.key === 'receivers'
    ? [...collection.items, ...externalReceiverItems.value]
    : collection.items;
}

const tabItems = computed<GaiaTabBarItem[]>(() =>
  collections.value.map(entry => ({
    key: entry.key,
    label: entry.label,
    count: itemsOf(entry).length,
  }))
);

const activeItems = computed(() => itemsOf(activeCollection.value));

// Viewers and receivers are loaded whole, so their search runs in memory.
const listQuery = ref<string>('');

// A search typed on one tab means nothing on the next.
watch(activeTab, () => {
  listQuery.value = '';
});

const filteredItems = computed(() => {
  const needle = listQuery.value.trim().toLowerCase();
  if (needle.length === 0) return activeItems.value;
  return activeItems.value.filter(
    item =>
      item.label.toLowerCase().includes(needle) ||
      (item.description ?? '').toLowerCase().includes(needle)
  );
});

const activeRemovableItems = computed<GaiaRemovableItem[]>(() =>
  filteredItems.value.map(item => ({
    key: item.uid,
    label: item.label,
    meta: item.description ?? undefined,
  }))
);

async function loadCollection(collection: LinkedCollection): Promise<void> {
  collection.loading = true;
  collection.error = null;
  try {
    collection.items = await collection.list(calendarUid.value);
  } catch (error) {
    logger.error(`Error loading calendar ${collection.label}:`, error);
    // Inline rather than a toast: every tab loads at once, and stacked
    // toasts say nothing about which tab is empty because it failed.
    collection.error =
      error instanceof Error ? error.message : `Failed to load ${collection.label}`;
  } finally {
    collection.loading = false;
  }
}

/**
 * Report what the server refused. The per-uid `error` strings are the point —
 * "2 failed" alone gives the user nothing to act on.
 */
function reportLinkResult(result: ContractManagementCalendarLinkResult, action: string): void {
  if (result.failed === 0) {
    message.success(`${action} successfully`);
    return;
  }
  const reasons = result.results
    .filter(item => !item.success)
    .map(item => item.error ?? 'unknown error');
  message.warning(
    `${action}: ${result.successful} succeeded, ${result.failed} failed. ${reasons.join(' ')}`
  );
}

async function handleRemove(collection: LinkedCollection, key: string): Promise<void> {
  if (!collection.remove) return;
  collection.loading = true;
  try {
    const result = await collection.remove(calendarUid.value, [key]);
    reportLinkResult(result, `Removed from ${collection.label}`);
    await loadCollection(collection);
  } catch (error) {
    logger.error(`Error removing from calendar ${collection.label}:`, error);
    message.error(error instanceof Error ? error.message : `Failed to update ${collection.label}`);
    collection.loading = false;
  }
}

// ── Calendar ────────────────────────────────────────────────────────────────

async function fetchCalendar(): Promise<void> {
  loading.value = true;
  loadError.value = null;
  try {
    calendar.value = await CalendarService.getById(calendarUid.value);
  } catch (error) {
    logger.error('Error loading calendar:', error);
    loadError.value = error instanceof Error ? error.message : 'Failed to load calendar';
  } finally {
    loading.value = false;
  }
}

// ── ICS feed ────────────────────────────────────────────────────────────────

const exportUrl = ref<string>('');
const regenerating = ref<boolean>(false);

async function fetchExportUrl(): Promise<void> {
  try {
    exportUrl.value = await CalendarService.getExportUrl(calendarUid.value);
  } catch (error) {
    logger.error('Error loading calendar link:', error);
    message.error(error instanceof Error ? error.message : 'Failed to fetch calendar link');
  }
}

async function handleRegenerateUrl(): Promise<void> {
  regenerating.value = true;
  try {
    exportUrl.value = await CalendarService.regenerateExportUrl(calendarUid.value);
    message.success('Calendar link regenerated');
  } catch (error) {
    logger.error('Error regenerating calendar link:', error);
    message.error(error instanceof Error ? error.message : 'Failed to regenerate calendar link');
  } finally {
    regenerating.value = false;
  }
}

async function handleCopyUrl(): Promise<void> {
  if (!exportUrl.value) return;
  await copy(exportUrl.value);
}

// ── Presentation ────────────────────────────────────────────────────────────

const emptyCell = '—';

function formatDateTime(raw: string | null | undefined): string {
  if (!raw) return emptyCell;
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return emptyCell;
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatReminder(weeks: number | null | undefined): string {
  if (weeks === null || weeks === undefined) return 'No reminder';
  return `${weeks} week(s) before`;
}

/** Title subline: when it last went out. */
const subline = computed(() => {
  if (!calendar.value) return '';
  return calendar.value.last_sent
    ? `Last sent ${formatDateTime(calendar.value.last_sent)}`
    : 'Never sent';
});

const exampleData = computed(() => calendar.value?.example_data ?? []);

/** The prompt opens clamped; only a long one gets the toggle. */
const PROMPT_CLAMP_LINES = 4;
const promptExpanded = ref(false);
const isLongPrompt = computed(() => {
  const prompt = calendar.value?.prompt ?? '';
  return prompt.length > 280 || prompt.split('\n').length > PROMPT_CLAMP_LINES;
});

const showShareModal = ref<boolean>(false);

/** Sharing adds viewers, so the Viewers tab is what changes. */
async function handleShared(): Promise<void> {
  const viewers = collections.value.find(entry => entry.key === 'viewers');
  if (viewers) await loadCollection(viewers);
}

function openEdit(): void {
  void router.push({
    name: 'contract-management-calendar-edit',
    params: { uid: calendarUid.value },
  });
}

function backToList(): void {
  void router.push({ name: 'contract-management-calendar' });
}

onMounted(async () => {
  // The list endpoints are not admin-gated — only add and remove are — so the
  // memberships load for everyone and the section renders read-only below.
  await Promise.all([
    fetchCalendar(),
    fetchExportUrl(),
    fetchSources(),
    ...collections.value.map(loadCollection),
  ]);
});

const cardBorder = gaiaNeutral.borderCard;
const cardSurface = gaiaNeutral.surface;
const cellBorder = gaiaNeutral.borderSubtle;
</script>

<template>
  <Layout :content-max-width="gaiaContentWidth.page">
    <template #breadcrumb>
      <n-breadcrumb-item>{{ navLabels.contractManagement }}</n-breadcrumb-item>
      <n-breadcrumb-item clickable>
        <router-link :to="{ name: 'contract-management-calendar' }">
          {{ navLabels.calendars }}
        </router-link>
      </n-breadcrumb-item>
      <n-breadcrumb-item>{{ calendar?.name ?? 'Calendar' }}</n-breadcrumb-item>
    </template>
    <n-flex vertical :size="20">
      <gaia-banner v-if="loadError" type="error" title="This calendar couldn’t be loaded">
        {{ loadError }}
        <template #actions>
          <gaia-button type="primary" size="small" @click="fetchCalendar">Retry</gaia-button>
          <gaia-button size="small" @click="backToList">Back to calendars</gaia-button>
        </template>
      </gaia-banner>

      <gaia-spinner v-else-if="loading && !calendar" :show="true" :style="{ minHeight: '200px' }" />

      <template v-else-if="calendar">
        <n-flex align="flex-start" :size="[20, 12]">
          <n-flex vertical :size="6" :style="{ minWidth: 0 }">
            <gaia-header :level="1" variant="display" size="screen" :style="{ margin: 0 }">
              {{ calendar.name }}
            </gaia-header>
            <gaia-text size="body" tone="tertiary">{{ subline }}</gaia-text>
          </n-flex>

          <n-flex align="center" :size="8" :style="{ marginLeft: 'auto' }">
            <!-- The disabled button swallows pointer events, so the tooltip
                 has to hang off a wrapper rather than the button itself. -->
            <gaia-tooltip :disabled="canManage" placement="bottom-end">
              <template #trigger>
                <span>
                  <gaia-button size="medium" :disabled="!canManage" @click="showShareModal = true">
                    <template #icon>
                      <n-icon :component="IconShare" />
                    </template>
                    Share
                  </gaia-button>
                </span>
              </template>
              {{ ADMIN_ONLY_HINT }}
            </gaia-tooltip>
            <gaia-tooltip :disabled="canManage" placement="bottom-end">
              <template #trigger>
                <span>
                  <gaia-button size="medium" :disabled="!canManage" @click="openEdit">
                    Edit
                  </gaia-button>
                </span>
              </template>
              {{ ADMIN_ONLY_HINT }}
            </gaia-tooltip>
          </n-flex>
        </n-flex>

        <!-- Configuration ------------------------------------------------- -->
        <n-flex vertical :size="10" :wrap="false">
          <gaia-section-header variant="rule">
            Configuration
            <template #extra>
              <gaia-button
                text
                size="tiny"
                :theme-overrides="gaiaTextButtonOverrides.ink"
                @click="configExpanded = !configExpanded"
              >
                <template #icon>
                  <n-icon :component="configExpanded ? IconChevronDown : IconChevronForward" />
                </template>
                {{ configExpanded ? 'Hide' : 'Show' }}
              </gaia-button>
            </template>
          </gaia-section-header>

          <!-- One bordered card, as the mock draws it: the label cells are a
               tinted track down the left of each pair, so the card reads as a
               table of settings rather than a stack of sentences. `overflow:
               hidden` is what clips that tint to the card's radius. -->
          <div v-if="configExpanded" class="config-card">
            <div class="field-grid">
              <gaia-field-row label="Name" variant="table">
                {{ calendar.name }}
              </gaia-field-row>

              <gaia-field-row label="Last sent" variant="table">
                <gaia-text size="dense" :tone="calendar.last_sent ? 'ink' : 'muted'">
                  {{ formatDateTime(calendar.last_sent) }}
                </gaia-text>
              </gaia-field-row>

              <gaia-field-row label="Created at" variant="table">
                {{ formatDateTime(calendar.created_at) }}
              </gaia-field-row>

              <gaia-field-row label="First reminder" variant="table">
                <gaia-text
                  size="dense"
                  :tone="calendar.first_reminder_weeks === null ? 'muted' : 'ink'"
                >
                  {{ formatReminder(calendar.first_reminder_weeks) }}
                </gaia-text>
              </gaia-field-row>

              <gaia-field-row label="Second reminder" variant="table">
                <gaia-text
                  size="dense"
                  :tone="calendar.second_reminder_weeks === null ? 'muted' : 'ink'"
                >
                  {{ formatReminder(calendar.second_reminder_weeks) }}
                </gaia-text>
              </gaia-field-row>
            </div>

            <!-- Seven settings do not pair evenly, and an empty half-row would
                 leave the card's hairline stopping mid-air, so the odd one out
                 runs full width with the long-form values. -->
            <gaia-field-row label="Updated at" variant="table">
              <gaia-text size="dense" :tone="calendar.updated_at ? 'ink' : 'muted'">
                {{
                  calendar.updated_at
                    ? formatDateTime(calendar.updated_at)
                    : 'Never edited since creation'
                }}
              </gaia-text>
            </gaia-field-row>

            <gaia-field-row v-if="exampleData.length > 0" label="Example data" variant="table">
              <n-flex :size="6" :wrap="true">
                <gaia-tag v-for="entry in exampleData" :key="entry" variant="muted" size="tiny">
                  {{ entry }}
                </gaia-tag>
              </n-flex>
            </gaia-field-row>

            <gaia-field-row label="Sources" variant="table">
              <gaia-text v-if="sourcesError" size="dense" tone="muted">
                Couldn’t load sources
              </gaia-text>
              <n-flex v-else-if="sourceTags.length > 0" :size="6" :wrap="true">
                <gaia-tag v-for="tag in sourceTags" :key="tag" variant="muted" size="tiny">
                  {{ tag }}
                </gaia-tag>
              </n-flex>
              <gaia-text v-else size="dense" tone="muted">
                {{
                  calendar.contract_criteria
                    ? 'Every contract its creator can read'
                    : 'Nothing picked'
                }}
              </gaia-text>
            </gaia-field-row>

            <gaia-field-row label="Fixed dates" variant="table">
              <n-flex v-if="fixedDateLabels.length > 0" :size="6" :wrap="true">
                <gaia-tag v-for="label in fixedDateLabels" :key="label" variant="muted" size="tiny">
                  {{ label }}
                </gaia-tag>
              </n-flex>
              <gaia-text v-else size="dense" tone="muted">None</gaia-text>
            </gaia-field-row>

            <!-- Last row in the card, so no divider under it. -->
            <gaia-field-row label="Prompt" variant="table" :divider="false">
              <gaia-text v-if="!calendar.prompt" size="dense" tone="muted">
                No custom prompt
              </gaia-text>
              <n-flex v-else vertical align="start" :size="6" :wrap="false">
                <n-ellipsis
                  v-if="isLongPrompt && !promptExpanded"
                  :line-clamp="PROMPT_CLAMP_LINES"
                  :tooltip="false"
                  :style="{ whiteSpace: 'pre-wrap' }"
                >
                  <gaia-text size="dense" tone="text">{{ calendar.prompt }}</gaia-text>
                </n-ellipsis>
                <gaia-text v-else size="dense" tone="text" :style="{ whiteSpace: 'pre-wrap' }">
                  {{ calendar.prompt }}
                </gaia-text>
                <gaia-button
                  v-if="isLongPrompt"
                  text
                  size="tiny"
                  :theme-overrides="gaiaTextButtonOverrides.ink"
                  @click="promptExpanded = !promptExpanded"
                >
                  {{ promptExpanded ? 'Show less' : 'Show more' }}
                </gaia-button>
              </n-flex>
            </gaia-field-row>
          </div>
        </n-flex>

        <!-- Preview --------------------------------------------------------- -->
        <n-flex vertical :size="12" :wrap="false">
          <gaia-section-header variant="rule">Preview</gaia-section-header>

          <n-flex align="center" :size="8" :wrap="false">
            <gaia-code-box :style="{ flex: '1 1 auto', minWidth: 0 }">
              {{ exportUrl || 'Loading calendar link…' }}
            </gaia-code-box>

            <gaia-popconfirm
              title="Regenerate the link?"
              confirm-label="Regenerate"
              @confirm="handleRegenerateUrl"
            >
              <template #trigger>
                <gaia-icon-button
                  :dimension="34"
                  title="Regenerate link"
                  :loading="regenerating"
                  :style="{ flex: 'none' }"
                >
                  <svg
                    width="15"
                    height="15"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.8"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <path d="M21 12a9 9 0 1 1-2.6-6.4M21 3v6h-6" />
                  </svg>
                </gaia-icon-button>
              </template>
              The old URL stops working for anyone who subscribed to it.
            </gaia-popconfirm>

            <gaia-icon-button
              :dimension="34"
              title="Copy link"
              :disabled="!exportUrl"
              :style="{ flex: 'none' }"
              @click="handleCopyUrl"
            >
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <rect x="9" y="9" width="11" height="11" rx="2" />
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
              </svg>
            </gaia-icon-button>
          </n-flex>

          <!-- The month first, then every contract with its dates beneath it.
               The filters sit over the list but narrow the month too. -->
          <gaia-calendar-month-preview
            ref="monthPreviewRef"
            :calendar-uid="calendarUid"
            :filters="previewFilters"
            @changed="eventsTableRef?.refresh()"
          />
          <gaia-calendar-preview-filters v-model:filters="previewFilters" />
          <gaia-calendar-events-table
            ref="eventsTableRef"
            :calendar-uid="calendarUid"
            :filters="previewFilters"
            :date-types="calendar.date_types ?? []"
            :has-prompt="Boolean(calendar.prompt?.trim())"
            @changed="monthPreviewRef?.refresh()"
          />
        </n-flex>

        <!-- Linked collections ---------------------------------------------- -->
        <n-flex vertical :size="14" :wrap="false">
          <gaia-tab-bar
            :items="tabItems"
            :active="activeTab"
            @select="(key: string) => (activeTab = key)"
          />

          <gaia-search-input
            v-if="activeItems.length > 0"
            v-model:value="listQuery"
            :placeholder="`Search ${activeCollection.label.toLowerCase()}`"
            :debounce="0"
          />

          <gaia-empty-state
            v-if="activeCollection.error"
            status="alert"
            :title="`Couldn’t load ${activeCollection.label.toLowerCase()}`"
          >
            {{ activeCollection.error }}
            <template #actions>
              <gaia-button size="small" @click="loadCollection(activeCollection)"
                >Retry</gaia-button
              >
            </template>
          </gaia-empty-state>

          <gaia-empty-state
            v-else-if="!activeCollection.loading && activeItems.length === 0"
            dashed
            :title="`No ${activeCollection.label.toLowerCase()} yet`"
          >
            {{ activeCollection.emptyText }}
          </gaia-empty-state>

          <gaia-empty-state
            v-else-if="!activeCollection.loading && filteredItems.length === 0"
            dashed
            :title="`No ${activeCollection.label.toLowerCase()} match`"
          >
            Try a different search.
            <template #actions>
              <gaia-button size="small" @click="listQuery = ''">Clear search</gaia-button>
            </template>
          </gaia-empty-state>

          <gaia-removable-list
            v-else
            :items="activeRemovableItems"
            :readonly="!canManage || !activeCollection.remove"
            @remove="(uid: string) => handleRemove(activeCollection, uid)"
          />
        </n-flex>
      </template>
    </n-flex>

    <mass-share-contracts-modal
      v-model:show="showShareModal"
      kind="calendar"
      :contract-uids="[calendarUid]"
      :contract-names="calendar?.name ? [calendar.name] : []"
      @shared="handleShared"
    />
  </Layout>
</template>

<style scoped>
/* The card the configuration rows sit in. `overflow: hidden` is load-bearing:
   it clips the label cells' tint to the radius, so the fill ends where the
   card does instead of squaring off its corners. */
.config-card {
  border: 1px solid v-bind(cardBorder);
  border-radius: 12px;
  overflow: hidden;
  background: v-bind(cardSurface);
}

/* Two label/value pairs per row above 1100px, one below — the mock's four
   tracks. No column gap: the label cells' tint is what separates the pairs, so
   a gap would open a white seam through the middle of the card. */
.field-grid {
  display: grid;
  grid-template-columns: 1fr;
}

@media (min-width: 1100px) {
  .field-grid {
    grid-template-columns: 1fr 1fr;
  }

  /* The hairline between the two pairs. It rides the left row rather than the
     right row's label cell so it stops at the same place as the row dividers. */
  .field-grid > :nth-child(odd) {
    border-right: 1px solid v-bind(cellBorder);
  }
}
</style>
